import { passwordSchema } from '@nexo/types';
import { Button, Field, Input, useToast } from '@nexo/ui';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage, translateMessage } from '../../lib/forms';
import { AuthLayout } from './AuthLayout';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; form?: string }>({});
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      const message = parsed.error.issues[0]?.message;
      return setErrors({ password: message && translateMessage(message) });
    }
    if (password !== confirm) return setErrors({ confirm: t('Las contraseñas no coinciden') });
    setLoading(true);
    try {
      await api.auth.resetPassword(token, password);
      toast({
        title: t('Contraseña actualizada'),
        description: t('Ya puedes iniciar sesión.'),
        tone: 'success',
      });
      void navigate('/login', { replace: true });
    } catch (err) {
      setErrors({ form: errorMessage(err) });
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title={t('Crea una nueva contraseña')}
      subtitle={t('Por seguridad, se cerrará la sesión en todos tus dispositivos.')}
      footer={
        <Link to="/forgot-password" className="font-medium text-accent hover:underline">
          {t('Pedir un enlace nuevo')}
        </Link>
      }
    >
      {!token ? (
        <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
          {t('El enlace no es válido.')}
        </p>
      ) : (
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          <Field
            label={t('Nueva contraseña')}
            error={errors.password}
            hint={t('Mínimo 8 caracteres')}
          >
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setErrors({});
                }}
              />
            )}
          </Field>
          <Field label={t('Repite la contraseña')} error={errors.confirm}>
            {(props) => (
              <Input
                {...props}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => {
                  setConfirm(e.target.value);
                  setErrors({});
                }}
              />
            )}
          </Field>
          {errors.form && (
            <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
              {errors.form}
            </p>
          )}
          <Button type="submit" size="lg" loading={loading} className="w-full">
            {t('Guardar contraseña')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
